import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  Grid2,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Chip,
  Paper,
  Link
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import PrimaryIcon from "Components/Common/PrimaryIcon";
import { ReactComponent as CloseIcon } from "Assets/Icons/Home/CloseIcon.svg";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from 'Slices/thunks';

function formatTimeTo12Hour(timeString = "") {
  const d = `1970-01-01 ${timeString}`
  const date = new Date(d);

  // Format to 12-hour format using toLocaleTimeString
  return date.toLocaleTimeString("default", {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDate(opt, i) {
  const date = new Date(); // current date
  const options = {
    day: "2-digit",
    month: "short",
  }

  const startDate = new Date(date);
  startDate.setDate(date.getDate() - opt.StartDay);

  const endDate = new Date(date);
  endDate.setDate(date.getDate() + opt.EndDay);

  return `${startDate.toLocaleDateString("default", options)} - ${endDate.toLocaleDateString("default", options)}`;
}

// --- Helper Component for selectable cards ---
const SelectableCard = ({ value, label, subLabel, icon, chipLabel, selectedValue, size = 6, onSelect }) => {
  const isSelected = value == selectedValue;

  return (
    <Grid item xs={12} sm={size}>
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: '12px',
          cursor: 'pointer',
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          borderWidth: isSelected ? '2px' : '1px',
          boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
        onClick={() => onSelect(value)}
      >
        <FormControlLabel
          value={value}
          control={<Radio />}
          label=""
          sx={{ mr: 0 }}
        />
        {icon && (
          <>
            <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
                <img src={icon} />
            </Box>

            {/* <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
                {icon}
                <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box> */}
          </>
        )}
        <Box flexGrow={1}>
          <Typography fontWeight="bold">{label}</Typography>
          {!icon && <Typography variant="body2" color="text.secondary">{subLabel}</Typography>}
        </Box>
        {chipLabel && <Chip label={chipLabel} sx={(theme) => ({bgcolor: theme.palette.background.default})} size="small" />}
      </Paper>
    </Grid>
  );
};

// --- Main Modal Component ---
const GoalDialog = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedPoint, setSelectedPoint] = useState();
  const [selectedTime, setSelectedTime] = useState();

  const { Profile, StreakTiming, Common, Translation } = useSelector((state) => ({
    Profile: state.Profile.profile,
    StreakTiming: state.Common.timing,
    Common: state.Common.common,
    Translation: state.Translation.translation?.Dashboard || {},
  }));

  const handleGoalChange = (event) => {
    setSelectedPoint(event.target.value);
  };

  const handleTimeChange = (event) => {
    setSelectedTime(event.target.value);
  };

  const handleSubmit = () => {
    dispatch(updateProfile({
      GoalID: selectedPoint,
      StreakTimeID: selectedTime
    }))
    // setOpen(false);
  }

  useEffect(() => {
    if (Profile) {
      setSelectedPoint(Profile?.GoalID)
      setSelectedTime(Profile?.StreakTimeID)
    }

  }, [Profile])

  return (
    <Dialog
      open={open}
      onClose={setOpen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="bold">
          {Translation?.EditYourGoal}
        </Typography>
        <IconButton onClick={() => setOpen()}>
          {" "}
          <PrimaryIcon
            icon={CloseIcon}
            color="white"
            height="14px"
            width="14px"
          />{" "}
        </IconButton>{" "}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 4, borderTop: '1px solid #E0E0E0' }}>
        {/* Set New Goal Section */}
        <Box mb={4}>
          <Typography variant="h6" fontSize={"18px"} fontWeight="bold" gutterBottom>
            {Translation?.SetYourNewGoal}
          </Typography>
          <Typography variant="body2" color="secondary.light" fontSize={"14px"} fontWeight={600} mb={2}>
            {Translation?.SettingDailyGoalForYourself}
          </Typography>
          <RadioGroup value={selectedPoint} onChange={handleGoalChange}>
            <Grid container spacing={2}>
              {(Common?.Goal || []).map((opt, i) => (
                <SelectableCard
                  key={opt.ID}
                  value={opt.ID}
                  label={opt.Value}
                  icon={opt.Icon}
                  chipLabel={i === 0 ? Translation?.Default : null}
                  selectedValue={selectedPoint}
                  onSelect={setSelectedPoint}
                />
              ))}
            </Grid>
          </RadioGroup>
        </Box>

        {/* Select Timings Section */}
        <Box>
          <Typography variant="h6" fontSize="18px" fontWeight="bold" gutterBottom>
            {Translation?.SelectStreakTimings}
          </Typography>
          <Typography variant="body2" color="secondary.light" fontSize={"14px"} fontWeight={600} mb={2}>
            {Translation?.AdjustYourStreakTimings}
          </Typography>

          { Profile?.TimezoneID &&
            <Paper
                elevation={0}
                sx={{
                p: 1,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#FFF4F4',
                border: '1px solid #FFCDCD',
                borderRadius: '8px',
                }}
            >
                <ErrorIcon sx={{ color: '#F44336' }} />
                <Typography variant="body" color="secondary.light" fontSize={"14px"} fontWeight={700}>
                    {Translation?.TimingMessageAlert?.replace("{XXX}", Profile?.GMTLabel)}
                    <Link underline="hover" sx={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => navigate('/settings')}> {Translation?.Settings}.</Link>
                </Typography>
            </Paper>
          }
          

          <RadioGroup value={selectedTime} onChange={handleTimeChange}>
            <Grid container spacing={2}>
              {StreakTiming.map((opt, i) => (
                <SelectableCard
                  key={opt.ID}
                  value={opt.ID}
                  label={`${formatTimeTo12Hour(opt.StartTime)} - ${formatTimeTo12Hour(opt.EndTime)}`}
                  subLabel={formatDate(opt, i)}
                  selectedValue={selectedTime}
                  onSelect={setSelectedTime}
                  size={4}
                />
              ))}
            </Grid>
          </RadioGroup>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedPoint || !selectedTime}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: '24px',
            textTransform: 'uppercase',
            fontWeight: 'bold',
            // color: 'white',
            // background: 'linear-gradient(90deg, #FF6B6B, #FF8E8E)',
            // '&:hover': {
            //   background: 'linear-gradient(90deg, #FF5252, #FF7979)',
            //   boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
            // },
          }}
        >
          {Translation?.UpdateGoalTime}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoalDialog;